import React, { useState, useEffect } from "react";
import {
  TextField,
  Typography,
  Box,
  Divider,
  Button,
  FormHelperText,
  FormControl,
  Select,
  InputLabel,
  FormControlLabel,
  Checkbox
} from "@material-ui/core";
import { Form, Field } from "react-final-form";
import { IntroText, FormActionDrawer } from "../../../components";
import createDecorator from "final-form-focus";
import { useHeader } from "../../../contexts";
import { string, object } from "yup";
import { useYupValidation } from "../../../hooks";
import { useTranslation } from "react-i18next";

const focusOnErrors = createDecorator();

const NOT_EMPLOYED_DEFAULTS = {
  occupation: "unknown",
  employer_name: "unknown",
  work_address: "unknown",
  employer_phone_number: "unknown"
};

const validationSchemaOccupation = object({
  occupation: string()
    .required()
    .trim()
    .label("Occupation"),
  employer_name: string()
    .required()
    .trim()
    .label("Employer name"),
  work_address: string()
    .required()
    .trim()
    .label("Work address"),
  employer_phone_number: string()
    .required()
    .trim()
    .label("Employer phone number")
});

const validationSchemaSchool = object({
  employer_name: string()
    .required()
    .trim()
    .label("School name"),
  occupation: string()
    .required()
    .trim()
    .label("Role"),
  work_address: string()
    .required()
    .trim()
    .label("School location")
});

const AttendsSchool = ({
  setShowSchoolForm,
  setShowOptionButtons,
  setShowOccupationForm
}) => {
  const { t } = useTranslation();

  return (
    <Box data-cy="attend-school-question">
      <IntroText>{t("schedule.occupation.main_intro_text")}</IntroText>
      <Button
        variant="contained"
        color="primary"
        fullWidth
        size="large"
        data-cy="school-yes"
        onClick={() => {
          setShowSchoolForm(true);
          setShowOptionButtons(false);
        }}
      >
        {t("schedule.occupation.yes")}
      </Button>
      <Box my={2}>
        <Divider variant="middle" />
      </Box>
      <Button
        variant="contained"
        fullWidth
        size="large"
        data-cy="school-no"
        onClick={() => {
          setShowOccupationForm(true);
          setShowOptionButtons(false);
        }}
      >
        {t("schedule.occupation.no")}
      </Button>
    </Box>
  );
};

const ScheduleOccupation = ({
  setDrawerClearance,
  updateScheduleData,
  isSubmitting
}) => {
  const { setTitle } = useHeader();
  const { t } = useTranslation();

  const [showOptionButtons, setShowOptionButtons] = useState(true);
  const [showSchoolForm, setShowSchoolForm] = useState(false);
  const [showOccupationForm, setShowOccupationForm] = useState(false);
  const [employed, setEmployed] = useState(true);

  useEffect(() => {
    setTitle("Occupation details");
  }, []);

  const { validate } = useYupValidation();

  const validateForm = async data => {
    if (employed) {
      const errors = await validate(
        showOccupationForm ? validationSchemaOccupation : validationSchemaSchool
      )(data);

      if (errors) return errors;
    } else {
      data = NOT_EMPLOYED_DEFAULTS;
    }

    updateScheduleData({
      ...data,
      school: showSchoolForm
    });
  };

  return (
    <Box>
      {showOptionButtons && (
        <AttendsSchool
          setShowSchoolForm={setShowSchoolForm}
          setShowOccupationForm={setShowOccupationForm}
          setShowOptionButtons={setShowOptionButtons}
        />
      )}
      {showOccupationForm && (
        <Form
          onSubmit={validateForm}
          decorators={[focusOnErrors]}
          render={({ handleSubmit, submitErrors: errors = {} }) => (
            <form data-cy="schedule-occupation-details" onSubmit={handleSubmit}>
              <IntroText>{t("schedule.occupation.intro_text")}</IntroText>
              <Typography paragraph align="center">
                {t("schedule.occupation.secondary_text")}
              </Typography>
              <Field
                name="occupation"
                render={({ input }) => (
                  <TextField
                    {...input}
                    label={t("schedule.occupation.form.occupation")}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    error={!!errors.occupation}
                    helperText={errors.occupation}
                    inputProps={{
                      "data-lpignore": "true",
                      "data-cy": "input-occupation"
                    }}
                    disabled={!employed}
                  />
                )}
              />
              <Field
                name="employer_name"
                render={({ input }) => (
                  <TextField
                    {...input}
                    label={t("schedule.occupation.form.empolyer_name")}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    error={!!errors.employer_name}
                    helperText={errors.employer_name}
                    inputProps={{
                      "data-lpignore": "true",
                      "data-cy": "input-employer-name"
                    }}
                    disabled={!employed}
                  />
                )}
              />
              <Field
                name="work_address"
                render={({ input }) => (
                  <TextField
                    {...input}
                    label={t("schedule.occupation.form.work_address")}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    error={!!errors.work_address}
                    helperText={errors.work_address}
                    inputProps={{
                      "data-lpignore": "true",
                      "data-cy": "input-work-address"
                    }}
                    disabled={!employed}
                  />
                )}
              />
              <Field
                name="employer_phone_number"
                render={({ input }) => (
                  <TextField
                    {...input}
                    label={t("schedule.occupation.form.employer_phone")}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    error={!!errors.employer_phone_number}
                    helperText={errors.employer_phone_number}
                    inputProps={{
                      "data-lpignore": "true",
                      "data-cy": "input-employer-phone-number"
                    }}
                    disabled={!employed}
                  />
                )}
              />
              <Box my={2}>
                <FormControlLabel
                  label={t("schedule.occupation.not_employed")}
                  labelPlacement="start"
                  control={
                    <Checkbox
                      checked={!employed}
                      onChange={() => setEmployed(employed => !employed)}
                      color="primary"
                    />
                  }
                />
              </Box>
              <Button
                data-cy="test-back"
                onClick={() => {
                  setShowOptionButtons(true);
                  setShowOccupationForm(false);
                  setEmployed(true);
                }}
              >
                {t("schedule.occupation.back_button")}
              </Button>
              <FormActionDrawer
                setDrawerClearance={setDrawerClearance}
                loading={isSubmitting}
              />
            </form>
          )}
        />
      )}
      {showSchoolForm && (
        <Form
          onSubmit={validateForm}
          decorators={[focusOnErrors]}
          render={({ handleSubmit, submitErrors: errors = {} }) => (
            <form data-cy="schedule-school-details" onSubmit={handleSubmit}>
              <IntroText>
                {t("schedule.occupation.intro_text_school")}
              </IntroText>
              <Typography paragraph align="center">
                {t("schedule.occupation.secondary_text_school")}
              </Typography>
              <Field
                name="employer_name"
                render={({ input }) => (
                  <TextField
                    {...input}
                    label={t("schedule.occupation.form_school.school_name")}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    error={!!errors.employer_name}
                    helperText={errors.employer_name}
                    inputProps={{
                      "data-lpignore": "true",
                      "data-cy": "input-occupation"
                    }}
                  />
                )}
              />
              <Field
                name="occupation"
                render={({ input }) => (
                  <FormControl margin="normal" fullWidth variant="outlined">
                    <InputLabel id="role">
                      {t("schedule.occupation.form_school.role")}
                    </InputLabel>
                    <Select
                      {...input}
                      native
                      labelId="role"
                      id="role"
                      label="Role"
                      fullWidth
                      inputProps={{ "data-cy": "input-level" }}
                    >
                      <option value=""></option>
                      <option value="faculty">
                        {t(
                          "schedule.occupation.form_school.role_options.faculty"
                        )}
                      </option>
                      <option value="staff">
                        {t(
                          "schedule.occupation.form_school.role_options.staff"
                        )}
                      </option>
                      <option value="student">
                        {t(
                          "schedule.occupation.form_school.role_options.student"
                        )}
                      </option>
                    </Select>
                    {!!errors.role && (
                      <FormHelperText error={true}>
                        {errors.occupation}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />
              <Field
                name="work_address"
                render={({ input }) => (
                  <TextField
                    {...input}
                    label={t("schedule.occupation.form_school.location")}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    error={!!errors.work_address}
                    helperText={errors.work_address}
                    inputProps={{
                      "data-lpignore": "true",
                      "data-cy": "input-employer-name"
                    }}
                  />
                )}
              />
              <Button
                data-cy="back-button"
                onClick={() => {
                  setShowOptionButtons(true);
                  setShowOccupationForm(false);
                  setShowSchoolForm(false);
                  setEmployed(true);
                }}
              >
                {t("schedule.occupation.back_button")}
              </Button>
              <FormActionDrawer
                setDrawerClearance={setDrawerClearance}
                loading={isSubmitting}
              />
            </form>
          )}
        />
      )}
    </Box>
  );
};

export { ScheduleOccupation };
